import {useEffect, useMemo} from 'react';
import {Helmet} from 'react-helmet-async';

import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {buildConsentDefaultScript} from '@amblique/king-living/app/utils/gtm-consent';
import {buildGtmLoaderScript, getGtmConfigFromSite, GTM_DATA_LAYER_INIT} from '@amblique/king-living/app/utils/gtm-utils';

/**
 * GT-004 script tags — rendered by GtmHead via react-helmet-async (SSR + CSR).
 *
 * @param {{ containerId: string, defaultConsentGranted?: boolean, enabled?: boolean }} config
 * @returns {import('react').ReactElement[]}
 */
export const renderGtmHeadScripts = ({containerId, defaultConsentGranted = false, enabled = true, siteId = ''}) => {
  if (!containerId || !enabled) {
    return [];
  }

  return [
    <script key="gtm-datalayer-init" id="gtm-datalayer-init">{GTM_DATA_LAYER_INIT}</script>,
    <script key="gtm-consent-default" id="gtm-consent-default">
      {buildConsentDefaultScript(defaultConsentGranted, siteId)}
    </script>,
    <script key="gtm-loader" id="gtm-loader">{buildGtmLoaderScript(containerId)}</script>
  ];
};

/**
 * GT-004 — dataLayer init, Consent Mode default (from site pref), GTM loader.
 */
const GtmHead = () => {
  const {site} = useMultiSite();
  const gtmConfig = useMemo(() => getGtmConfigFromSite(site), [site]);

  useEffect(() => {
    const {containerId, defaultConsentGranted, enabled, siteId} = gtmConfig;

    if (!enabled || !containerId) {
      return;
    }

    if (!window.__klGtmConsentDefaultSet) {
      Function(buildConsentDefaultScript(defaultConsentGranted, siteId))();
    }

    if (window.__klGtmLoaderInjected === containerId) {
      return;
    }

    Function(buildGtmLoaderScript(containerId))();
    window.__klGtmLoaderInjected = containerId;
  }, [gtmConfig.containerId, gtmConfig.defaultConsentGranted, gtmConfig.enabled, gtmConfig.siteId]);

  if (!gtmConfig.enabled) {
    return null;
  }

  return (
    <Helmet defer={false}>
      {renderGtmHeadScripts(gtmConfig)}
    </Helmet>
  );
};

GtmHead.displayName = 'GtmHead';

export default GtmHead;
